// frontend/src/components/Dashboard/RightSidebar.js
import React, { useState, useEffect, useRef } from 'react';
import { getUniqueTopics, getAllSources, getCommonTopics } from '../../services/api'; // Added getCommonTopics
import { ChevronDownIcon, ChevronUpIcon, XCircleIcon, CalendarDaysIcon, TagIcon, ListBulletIcon, FunnelIcon } from '@heroicons/react/24/outline';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css"; // Ensure this CSS is imported for DatePicker styling
import { motion, AnimatePresence } from 'framer-motion'; // Import motion and AnimatePresence

const RightSidebar = ({ onFilterChange, currentFilters, isOpen }) => {
  // State for locally selected filter values before applying
  const [localSelectedTopics, setLocalSelectedTopics] = useState([]);
  const [localSelectedSources, setLocalSelectedSources] = useState([]);
  const [localStartDate, setLocalStartDate] = useState(null);
  const [localEndDate, setLocalEndDate] = useState(null);

  // State for topic search and suggestions
  const [topicSearchTerm, setTopicSearchTerm] = useState('');
  const [allAvailableTopics, setAllAvailableTopics] = useState([]);
  const [topicSuggestions, setTopicSuggestions] = useState([]);
  const [isTopicSuggestionsVisible, setIsTopicSuggestionsVisible] = useState(false);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsError, setTopicsError] = useState('');
  const topicInputRef = useRef(null); // Ref for topic input
  const topicSuggestionsRef = useRef(null); // Ref for topic suggestions dropdown

  // State for source search and suggestions
  const [sourceSearchTerm, setSourceSearchTerm] = useState('');
  const [allAvailableSources, setAllAvailableSources] = useState([]);
  const [sourceSuggestions, setSourceSuggestions] = useState([]);
  const [isSourceSuggestionsVisible, setIsSourceSuggestionsVisible] = useState(false);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesError, setSourcesError] = useState('');
  const sourceInputRef = useRef(null); // Ref for source input
  const sourceSuggestionsRef = useRef(null); // Ref for source suggestions dropdown
  
  // State for popular topics
  const [commonTopics, setCommonTopics] = useState([]);
  const [isLoadingCommonTopics, setIsLoadingCommonTopics] = useState(false);
  const [commonTopicsError, setCommonTopicsError] = useState('');
  
  // State for expanding/collapsing filter groups
  const [filterGroupsExpanded, setFilterGroupsExpanded] = useState({
    date: true,
    topics: true,
    sources: true,
    popularTopics: true,
  });

  // Consistent navbar height, adjust if your actual navbar height differs
  const navbarHeight = "88px"; 

  // Toggles the expanded state of a filter group
  const toggleFilterGroup = (groupName) => {
    setFilterGroupsExpanded(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  // Effect to synchronize local filter state with `currentFilters` prop from parent
  useEffect(() => {
    const newTopics = currentFilters.topics ? currentFilters.topics.split(',').filter(t => t.trim() !== '') : [];
    setLocalSelectedTopics(newTopics);
    
    const currentSourceIds = currentFilters.source_ids ? currentFilters.source_ids.split(',').map(id => parseInt(id, 10)) : [];
    if (allAvailableSources.length > 0) {
        setLocalSelectedSources(allAvailableSources.filter(s => currentSourceIds.includes(s.id)));
    } else if (currentSourceIds.length > 0 && !sourcesLoading) {
        setLocalSelectedSources(currentSourceIds.map(id => ({ id, name: `Source ID ${id}` })));
    } else if (!currentFilters.source_ids) { 
        setLocalSelectedSources([]);
    }
    
    const parseDateSafe = (dateStr) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? null : d; 
    };
    setLocalStartDate(parseDateSafe(currentFilters.publication_date_after));
    setLocalEndDate(parseDateSafe(currentFilters.publication_date_before));

  }, [currentFilters, allAvailableSources, allAvailableTopics, sourcesLoading]);

  // Effect to fetch initial data (topics and sources) for the filter dropdowns
  useEffect(() => {
    const fetchInitialData = async () => {
      setTopicsLoading(true); 
      setTopicsError('');
      try {
        const topicsData = await getUniqueTopics();
        console.log('All available topics fetched:', topicsData); // Add this log
        setAllAvailableTopics(topicsData || []); 
      } catch (error) { 
        console.error("Full error details:", error); // Enhanced error logging
        setTopicsError('Failed to load topics.'); 
      } finally { 
        setTopicsLoading(false); 
      }

      // Source fetching REMOVED - source filtering is hidden for data source privacy
      // If you need to re-enable source filtering in the future, uncomment the UI section
      // and this fetching logic, and adjust SourceViewSet permissions accordingly.

      // setSourcesLoading(true);
      // setSourcesError('');
      // try {
      //   const sourcesData = await getAllSources();
      //   setAllAvailableSources(Array.isArray(sourcesData) ? sourcesData.map(s => ({ id: s.id, name: s.name })) : []);
      // } catch (error) {
      //   console.error("Full error details:", error);
      //   setSourcesError('Failed to load sources.');
      // } finally {
      //   setSourcesLoading(false);
      // }

      // Fetch common topics
      setIsLoadingCommonTopics(true);
      setCommonTopicsError('');
      try {
        const commonTopicsData = await getCommonTopics(15); // Limit to top 15
        console.log('Common topics fetched:', commonTopicsData);
        setCommonTopics(commonTopicsData || []);
      } catch (error) {
        console.error("Error fetching common topics:", error);
        setCommonTopicsError('Failed to load popular topics.');
      } finally {
        setIsLoadingCommonTopics(false);
      }
    };
    fetchInitialData();
  }, []); 

  // Handler to apply the currently selected local filters
  const handleApplyFilters = () => {
    const newFilters = {
      topics: localSelectedTopics.join(','),
      // source_ids: localSelectedSources.map(s => s.id).join(','), // REMOVED: Hide source filtering to protect data source privacy
      publication_date_after: localStartDate ? localStartDate.toISOString().split('T')[0] : '', 
      publication_date_before: localEndDate ? localEndDate.toISOString().split('T')[0] : '',   
    };
    const activeFilters = {};
    for (const key in newFilters) {
      if (newFilters[key] !== '' && newFilters[key] !== null && newFilters[key] !== undefined && !(Array.isArray(newFilters[key]) && newFilters[key].length === 0)) {
        activeFilters[key] = newFilters[key];
      }
    }
    onFilterChange(activeFilters); 
  };

  // Handler to clear all local filters and notify parent
  const handleClearAllFilters = () => {
    setLocalSelectedTopics([]);
    setLocalSelectedSources([]);
    setLocalStartDate(null);
    setLocalEndDate(null);
    setTopicSearchTerm('');
    setSourceSearchTerm('');
    setIsTopicSuggestionsVisible(false);
    setIsSourceSuggestionsVisible(false);
    onFilterChange({}); 
  };

  // Handler for topic search input changes
  const handleTopicSearchChange = (e) => {
    const term = e.target.value;
    setTopicSearchTerm(term);
    if (term.trim().length > 0 && allAvailableTopics.length > 0) {
      const filtered = allAvailableTopics.filter(
        topic => topic.toLowerCase().includes(term.toLowerCase()) && !localSelectedTopics.includes(topic)
      );
      setTopicSuggestions(filtered); // Show all filtered suggestions
      setIsTopicSuggestionsVisible(filtered.length > 0);
    } else {
      setTopicSuggestions([]);
      setIsTopicSuggestionsVisible(false);
    }
    // Attempt to re-focus the input after state updates
    setTimeout(() => topicInputRef.current?.focus(), 0);
  };
  
  // Adds a selected topic to the local filter state
  const addTopic = (topic) => {
    if (!localSelectedTopics.includes(topic)) {
      setLocalSelectedTopics(prev => [...prev, topic]);
    }
    setTopicSearchTerm(''); setTopicSuggestions([]); setIsTopicSuggestionsVisible(false);
    setTimeout(() => topicInputRef.current?.focus(), 0); // Re-focus after adding
  };

  // Removes a topic from the local filter state
  const removeTopic = (topicToRemove) => {
    setLocalSelectedTopics(prev => prev.filter(topic => topic !== topicToRemove));
    setTimeout(() => topicInputRef.current?.focus(), 0); // Re-focus after removing
  };

  // Handler for selecting a popular topic
  const handlePopularTopicSelect = (topic) => {
    if (!localSelectedTopics.includes(topic)) {
      setLocalSelectedTopics(prev => [...prev, topic]);
    }
  };

  // Helper function to capitalize each word
  const capitalizeEachWord = (str) => str ? str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : "";

  // Handler for source search input changes
  const handleSourceSearchChange = (e) => {
    const term = e.target.value;
    setSourceSearchTerm(term);
    if (term.trim().length > 0 && allAvailableSources.length > 0) {
      const filtered = allAvailableSources.filter(
        source => source.name.toLowerCase().includes(term.toLowerCase()) &&
                  !localSelectedSources.find(selected => selected.id === source.id) 
      );
      setSourceSuggestions(filtered); // Show all filtered suggestions
      setIsSourceSuggestionsVisible(filtered.length > 0);
    } else {
      setSourceSuggestions([]);
      setIsSourceSuggestionsVisible(false);
    }
    // Attempt to re-focus the input after state updates
    setTimeout(() => sourceInputRef.current?.focus(), 0);
  };

  // Adds a selected source to the local filter state
  const addSource = (source) => { 
    if (!localSelectedSources.find(selected => selected.id === source.id)) {
      setLocalSelectedSources(prev => [...prev, source]);
    }
    setSourceSearchTerm(''); setSourceSuggestions([]); setIsSourceSuggestionsVisible(false);
    setTimeout(() => sourceInputRef.current?.focus(), 0); // Re-focus after adding
  };

  // Removes a source from the local filter state
  const removeSource = (sourceToRemove) => { 
    setLocalSelectedSources(prev => prev.filter(source => source.id !== sourceToRemove.id));
    setTimeout(() => sourceInputRef.current?.focus(), 0); // Re-focus after removing
  };

  // Effect to handle clicks outside of suggestion dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (topicSuggestionsRef.current && !topicSuggestionsRef.current.contains(event.target) &&
          topicInputRef.current && !topicInputRef.current.contains(event.target)) {
        setIsTopicSuggestionsVisible(false);
      }
      if (sourceSuggestionsRef.current && !sourceSuggestionsRef.current.contains(event.target) &&
          sourceInputRef.current && !sourceInputRef.current.contains(event.target)) {
        setIsSourceSuggestionsVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside); 
  }, []); 

  // Variants for suggestion list animation
  const suggestionListVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.15 } }
  };
  
  // Variants for sidebar slide-in/out
  const sidebarVariants = {
    open: { x: 0 },
    closed: { x: "100%" },
  };

  const sidebarTransition = {
    type: "tween",
    ease: "easeInOut",
    duration: 0.3,
  };

  // Reusable component for rendering a filter section with Framer Motion for content
  const FilterSection = ({ title, children, groupName, icon: Icon }) => {
    const isExpanded = filterGroupsExpanded[groupName];
    return (
      <div className="py-3 border-b border-gray-200 last:border-b-0">
        <button onClick={() => toggleFilterGroup(groupName)} className="w-full flex justify-between items-center py-2 text-left focus:outline-none group">
          <span className="text-sm font-semibold text-gray-700 group-hover:text-blue-600 flex items-center">
            {Icon && <Icon className="w-5 h-5 mr-2 text-gray-400 group-hover:text-blue-500 transition-colors"/>} {title}
          </span>
          <motion.div animate={{ rotate: isExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
            <ChevronDownIcon className="w-5 h-5 text-gray-500 group-hover:text-blue-500" />
          </motion.div>
        </button>
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              key="content"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: "auto", marginTop: '0.75rem' },
                collapsed: { opacity: 0, height: 0, marginTop: '0rem' }
              }}
              transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
            >
              <div className="space-y-3 pl-1 pr-1">{children}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };


  return (
    <motion.div // Changed from div to motion.div
      className={`fixed right-0 bg-white shadow-2xl z-30 flex flex-col rounded-lg`}
      style={{ top: navbarHeight, height: `calc(100vh - ${navbarHeight})`, width: '18rem' }} 
      variants={sidebarVariants}
      initial="closed"
      animate={isOpen ? "open" : "closed"}
      transition={sidebarTransition}
      aria-hidden={!isOpen} 
    >
      {/* Sidebar Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">Filter Options</h3>
      </div>

      {/* Scrollable content area for filters */}
      <div className="flex-grow overflow-y-auto px-4 custom-scrollbar">
        {/* Date Filter Section */}
        <FilterSection title="Publication Date" groupName="date" icon={CalendarDaysIcon}>
            <div className="space-y-2">
                <div>
                    <label htmlFor="start-date-sidebar" className="text-xs font-medium text-gray-600 block mb-0.5">From:</label>
                    <DatePicker 
                        id="start-date-sidebar" 
                        selected={localStartDate} 
                        onChange={(date) => setLocalStartDate(date)} 
                        selectsStart 
                        startDate={localStartDate} 
                        endDate={localEndDate} 
                        dateFormat="MM/dd/yyyy" 
                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 shadow-sm" 
                        placeholderText="Start Date" 
                        isClearable 
                    />
                </div>
                <div>
                    <label htmlFor="end-date-sidebar" className="text-xs font-medium text-gray-600 block mb-0.5">To:</label>
                    <DatePicker 
                        id="end-date-sidebar" 
                        selected={localEndDate} 
                        onChange={(date) => setLocalEndDate(date)} 
                        selectsEnd 
                        startDate={localStartDate} 
                        endDate={localEndDate} 
                        minDate={localStartDate} 
                        dateFormat="MM/dd/yyyy" 
                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 shadow-sm" 
                        placeholderText="End Date" 
                        isClearable 
                    />
                </div>
            </div>
        </FilterSection>

        {/* Topics Filter Section */}
        <FilterSection title="Topics" groupName="topics" icon={TagIcon}>
            <div className="relative" ref={topicSuggestionsRef}> 
              <input 
                id="topic-search-sidebar" 
                type="text" 
                ref={topicInputRef} 
                value={topicSearchTerm}
                onChange={handleTopicSearchChange}
                onFocus={() => setIsTopicSuggestionsVisible(topicSearchTerm.trim().length > 0 && topicSuggestions.length > 0)} 
                placeholder={topicsLoading ? "Loading..." : "Search & add topics"}
                disabled={topicsLoading} 
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              />
              {topicsError && <p className="text-xs text-red-500 mt-1">{topicsError}</p>}
              <AnimatePresence>
                {(isTopicSuggestionsVisible && topicSuggestions.length > 0) && (
                  <motion.ul 
                    key="topic-suggestions"
                    variants={suggestionListVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="absolute z-40 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg max-h-36 overflow-y-auto custom-scrollbar"
                  >
                    {topicSuggestions.map((s, i) => <li key={`topic-sugg-${i}`} onClick={() => addTopic(s)} className="px-3 py-1.5 hover:bg-blue-50 cursor-pointer text-sm">{s}</li>)}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1.5 max-h-28 overflow-y-auto custom-scrollbar pr-1">
              {localSelectedTopics.map((t, i) => (
                <span key={`topic-sel-${i}`} className="flex items-center bg-blue-100 text-blue-700 text-xs font-medium px-2 py-1 rounded-full shadow-sm">
                  {t} <button onClick={() => removeTopic(t)} className="ml-1.5 text-blue-500 hover:text-blue-700 focus:outline-none">&times;</button>
                </span>
              ))}
            </div>
        </FilterSection>

        {/* Sources Filter Section - HIDDEN to protect data source privacy */}
        {/* 
        <FilterSection title="Sources" groupName="sources" icon={ListBulletIcon}>
            <div className="relative" ref={sourceSuggestionsRef}> 
              <input 
                id="source-search-sidebar" 
                type="text" 
                ref={sourceInputRef} 
                value={sourceSearchTerm}
                onChange={handleSourceSearchChange} 
                maxLength={50} 
                onFocus={() => setIsSourceSuggestionsVisible(sourceSearchTerm.trim().length > 0 && sourceSuggestions.length > 0)} 
                placeholder={sourcesLoading ? "Loading..." : "Search & add sources"}
                disabled={sourcesLoading} 
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              />
              {sourcesError && <p className="text-xs text-red-500 mt-1">{sourcesError}</p>}
              <AnimatePresence>
                {(isSourceSuggestionsVisible && sourceSuggestions.length > 0) && (
                  <motion.ul 
                    key="source-suggestions"
                    variants={suggestionListVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="absolute z-40 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg max-h-36 overflow-y-auto custom-scrollbar"
                  >
                    {sourceSuggestions.map((s, i) => <li key={s.id || `source-sugg-${i}`} onClick={() => addSource(s)} className="px-3 py-1.5 hover:bg-blue-50 cursor-pointer text-sm">{s.name}</li>)}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1.5 max-h-28 overflow-y-auto custom-scrollbar pr-1">
              {localSelectedSources.map((s, i) => (
                <span key={s.id || `source-sel-${i}`} className="flex items-center bg-teal-100 text-teal-700 text-xs font-medium px-2 py-1 rounded-full shadow-sm">
                  {s.name} <button onClick={() => removeSource(s)} className="ml-1.5 text-teal-500 hover:text-teal-700 focus:outline-none">&times;</button>
                </span>
              ))}
            </div>
        </FilterSection>
        */}

        {/* Popular Topics Section */}
        <FilterSection title="Popular Topics" groupName="popularTopics" icon={TagIcon}>
            {isLoadingCommonTopics && (
              <div className="text-center py-2">
                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <p className="text-xs text-gray-500 mt-1">Loading topics...</p>
              </div>
            )}
            {commonTopicsError && (
              <p className="text-xs text-red-500">{commonTopicsError}</p>
            )}
            {!isLoadingCommonTopics && !commonTopicsError && commonTopics.length > 0 && (
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto custom-scrollbar pr-1">
                {commonTopics.map((topic, index) => {
                  const isSelected = localSelectedTopics.includes(topic);
                  return (
                    <button
                      key={`popular-topic-${index}`}
                      onClick={() => handlePopularTopicSelect(topic)}
                      disabled={isSelected}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                        isSelected 
                          ? 'bg-blue-100 text-blue-700 cursor-not-allowed opacity-60' 
                          : 'bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-600 cursor-pointer'
                      }`}
                    >
                      {capitalizeEachWord(topic)}
                    </button>
                  );
                })}
              </div>
            )}
            {!isLoadingCommonTopics && !commonTopicsError && commonTopics.length === 0 && (
              <p className="text-xs text-gray-500">No popular topics available.</p>
            )}
        </FilterSection>
      </div>

      {/* Action Buttons */}
      <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-3">
        <button onClick={handleApplyFilters}
          className="w-full inline-flex items-center justify-center px-4 py-2.5 border border-transparent text-sm font-semibold rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">
          <FunnelIcon className="w-5 h-5 mr-2" /> Apply Filters
        </button>
        <button onClick={handleClearAllFilters}
          className="w-full inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 text-sm font-semibold rounded-lg text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors">
          <XCircleIcon className="w-5 h-5 mr-2 text-gray-500"/> Clear All Filters
        </button>
      </div>
    </motion.div> // End of motion.div for sidebar
  );
};

export default RightSidebar;
